import React, { useState } from 'react';
import { Box } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import { useTranslation } from 'react-i18next';
import { TEXT_COLORS, BORDER_COLORS, BG_COLORS } from '../../utils/colors';
import FilterDrawer from './FilterDrawer';

export interface ResponsiveFilterBarProps {
  /** `useMediaQuery(theme.breakpoints.up('sm'))` со страницы */
  isSm: boolean;
  /**
   * Контролы фильтра: TextField / Select / DateTimePicker / кнопка сброса.
   * Функцией — если контролу нужно закрыть drawer после выбора значения.
   */
  children: React.ReactNode | ((close: () => void) => React.ReactNode);
  /** Поле, которое остаётся в строке и на мобильных (обычно поиск) */
  alwaysVisible?: React.ReactNode;
  /**
   * Кнопки действий (обычно «Сбросить»): в общем ряду на десктопе и в закреплённом низу
   * шторки на мобиле. Внутри полей им не место — там они уезжают со скроллом и выглядят
   * мелкой ссылкой посреди формы.
   */
  actions?: React.ReactNode;
}

/**
 * Строка фильтров: на десктопе — обычный ряд, на мобильных — кнопка + нижний drawer
 * (эталон — `ShopPage` в личном кабинете, разбор в `docs/responsive.md` §3.4).
 *
 * Ряд из двух DateTimePicker и трёх Select не ужимается до 375px никакими средствами,
 * поэтому на xs он целиком переезжает в drawer, а в строке остаётся только кнопка
 * (и поиск, если он передан в `alwaysVisible`).
 */
export const ResponsiveFilterBar: React.FC<ResponsiveFilterBarProps> = ({
  isSm,
  children,
  alwaysVisible,
  actions,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const close = () => setOpen(false);
  const content = typeof children === 'function' ? children(close) : children;

  if (isSm) {
    return (
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'stretch', flexWrap: 'wrap', mb: 2 }}>
        {alwaysVisible}
        {content}
        {actions}
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'stretch', mb: 1.5 }}>
        <Box
          onClick={() => setOpen(true)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            flex: alwaysVisible ? '0 0 auto' : 1,
            border: `1px solid ${BORDER_COLORS.LIGHT}`,
            borderRadius: '4px',
            px: 1,
            py: 0.75,
            cursor: 'pointer',
            color: TEXT_COLORS.MUTED,
            fontSize: '0.75rem',
            '&:hover': { bgcolor: BG_COLORS.GLASS, borderColor: BORDER_COLORS.LIGHTER },
          }}
        >
          <TuneIcon sx={{ fontSize: '1rem' }} />
          {!alwaysVisible && t('common:filters')}
        </Box>
        {alwaysVisible}
      </Box>

      <FilterDrawer open={open} onClose={close} actions={actions}>
        {content}
      </FilterDrawer>
    </>
  );
};

export default ResponsiveFilterBar;
